import { NearbyBusinessCategory } from "common/types/nearby-businesses";
import { cn } from "@/lib/utils";
import { useState, useMemo, useRef, useEffect } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import AnalyticsService from "@/services/analytics";
import { ViewPager, Frame, Track, View } from "react-view-pager";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import VenueDetails from "@/features/home/VenueDetails";
import MapViewForNearby from "@/features/home/MapViewForNearby";
import {
  useI18N,
  useSavedPlaces,
  useAuth,
  useNearByFilters,
} from "common/hooks";
import VenueCard from "@/components/template/VenueCard";
import { Venue } from "common/types";
import { useAtom } from "jotai";

import {
  filtersOpenAtom,
  leftPanelExpandedAtom,
  mobileListViewAtom,
} from "@/state/atoms";
import {
  ArrowLeft,
  ArrowUp,
  ListBullets,
  MapTrifold,
} from "phosphor-react";
import { Modal } from "react-responsive-modal";
import CreateList from "@/components/common/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/common/SavedPlaces/AddRemoveVenueToList";
import NearbyFilterbar from "./NearbyFilterbar";
import NearbyFiltersSheet from "./NearbyFiltersSheet";
import NoSearchResultsFilter from "@/assets/images/noresultsfilter.svg";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";


interface NearbyVenuesProps {
  categories: NearbyBusinessCategory[];
  nearbyBusinessList: any[];
  venueLatitude: number;
  venueLongitude: number;
  venueId: string;
  nearbyFilterAccessibility: any[];
  accessibilityFeaturesRequirementsHiddenIds: string[];
  accessibilityFeaturesRequirementsShowHiddenById: string;
}

function NearbyVenuesNoFilterResults() {
  const i18n = useI18N();
  const [, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);
  const noFilterResultsRef = useRef(null);

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center w-full mt-5 px-4 md:px-0"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt={i18n.get("noMatches")}
        className="md:max-w-[50%] max-w-[80%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef}
        className="mt-3 py-2 md:py-0 text-center desktop-h4-medium-bold text-primary-800 dark:text-primary-150"
        tabIndex={0}
      >
        {i18n.get("noMatches")}
      </p>
      <p
        className="paragraph-small-regular py-2 md:py-0 max-w-[444px] mt-2 text-center text-natural-700 dark:text-primary-10"
        tabIndex={0}
      >
        {i18n.get("tryUpdateFilters")}
      </p>

      <div
        className="flex justify-center items-center w-full py-6 md:py-3 max-w-[200px]"
        tabIndex={0}
      >
        <WideButton
          className="grow"
          onClick={() => {
            setIsFilterSheetOpen(true);
          }}
        >
          {i18n.get("updateFilters")}
        </WideButton>
      </div>
    </div>
  );
}

function NearbyVenuesNoCategoryResults({
  handleCategorySectionTracking,
  setSelectedSection,
}: {
  handleCategorySectionTracking: (section: string) => void;
  setSelectedSection: (section: string) => void;
}) {
  const i18n = useI18N();
  const noFilterResultsRef = useRef(null);

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center w-full mt-5 px-4 md:px-0"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt={i18n.get("noMatches")}
        className="md:max-w-[50%] max-w-[80%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef}
        className="mt-3 py-2 md:py-0 text-center desktop-h4-medium-bold text-primary-800 dark:text-primary-150"
        tabIndex={0}
      >
        {i18n.get("noNearByVenuesInThisCategory")}
      </p>
      <p
        className="paragraph-small-regular py-2 md:py-0 max-w-[444px] mt-2 text-center text-natural-700 dark:text-primary-10"
        tabIndex={0}
      >
        {i18n.get("tryUpdateCategoryFilters")}
      </p>

      <div
        className="flex justify-center items-center w-full py-6 md:py-3 max-w-[200px]"
        tabIndex={0}
      >
        <WideButton
          className="grow"
          onClick={() => {
            handleCategorySectionTracking("All");
            setSelectedSection("All");
          }}
        >
          {i18n.get("viewAllNearbyVenues")}
        </WideButton>
      </div>
    </div>
  );
}

export function NearbyVenues({
  categories,
  nearbyBusinessList,
  venueLatitude,
  venueLongitude,
  venueId,
  nearbyFilterAccessibility,
  accessibilityFeaturesRequirementsHiddenIds,
  accessibilityFeaturesRequirementsShowHiddenById,
}: NearbyVenuesProps) {
  const [selectedSection, setSelectedSection] = useState<string>("All");
  const [currentVenueTypePage, setCurrentVenueTypePage] = useState(0);
  const track = useRef<Track>(null);
  const i18n = useI18N();
  const [isLeftPaneVisible] = useAtom(leftPanelExpandedAtom);
  const venueDetailsScrollRef = useRef<HTMLDivElement>(null);
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const trackMobile = useRef<Track>(null);
  const [mobileListView, setMobileListView] = useAtom(mobileListViewAtom);
  const mobileVenueDetailsScrollRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  const { refetch, data } = useSavedPlaces();
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const savedPlaces = data?.saved_places || [];
  const [listNameCreated, setListNameCreated] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);

  const [isFilterSheetOpen, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);

  const { filtersState } = useNearByFilters();

  // Search functionality
  const [searchTerm, setSearchTerm] = useState("");

  // Effect to detect when filters are applied
  useEffect(() => {
    // When filters are applied, reset to "All" category
    if (filtersState.isFiltersApplied) {
      // console.log('Filters were applied, resetting to All category');
      setSelectedSection("All");
    }
  }, [filtersState.isFiltersApplied]);

  // Apply filters to nearby businesses
  const filteredNearbyBusinesses = useMemo(() => {
    console.log(
      "Filtering businesses with options:",
      filtersState.accessibilityOptions
    );

    if (
      !filtersState?.accessibilityOptions ||
      filtersState.accessibilityOptions.length === 0
    ) {
      console.log("No filters applied, showing all venues");
      return nearbyBusinessList;
    }

    // Get selected tag IDs
    const selectedTagIds = filtersState.accessibilityOptions;

    // Convert string IDs to numbers for comparison
    const hiddenIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id)
    );
    const parentId = Number(accessibilityFeaturesRequirementsShowHiddenById);

    // Separate tags into step-free options and other options
    const stepFreeOptionIds = selectedTagIds.filter(
      (tagId) => hiddenIds.includes(tagId) || tagId === parentId
    );

    const otherOptionIds = selectedTagIds.filter(
      (tagId) => !hiddenIds.includes(tagId) && tagId !== parentId
    );

    console.log("Filter applied:", {
      stepFreeOptionIds,
      otherOptionIds,
      selectedTagIds,
      hiddenIds,
      parentId,
    });

    // Apply filters to businesses
    return nearbyBusinessList.filter((business) => {
      // Extract business tag IDs from the tagIds string
      // Assuming tag_ids is a string like "1|2|3|4"
      const tagIdsString = business.tag_ids || "";
      const businessTagIds = tagIdsString
        ? tagIdsString.split(",").map((id) => Number(id))
        : [];

      // console.log(`Checking business ${business.id} with tagIds:`, tagIdsString, 'parsed:', businessTagIds);

      // Check step-free options with OR logic
      const hasStepFreeOption =
        stepFreeOptionIds.length === 0 ||
        stepFreeOptionIds.some((filterId) => businessTagIds.includes(filterId));

      // Check other options with AND logic
      const hasOtherOptions =
        otherOptionIds.length === 0 ||
        otherOptionIds.every((filterId) => businessTagIds.includes(filterId));

      // Business must satisfy both conditions
      const result = hasStepFreeOption && hasOtherOptions;

      // if (result) {
      //   console.log(`Business ${business.id} MATCHES filters`);
      // }

      return result;
    });
  }, [
    nearbyBusinessList,
    filtersState.accessibilityOptions,
    accessibilityFeaturesRequirementsHiddenIds,
    accessibilityFeaturesRequirementsShowHiddenById,
  ]);

  useEffect(() => {
    if (selectedVenue) {
      onVenueClick(selectedVenue);
    } else {
      track.current?.prev();
      trackMobile.current?.prev();
      setCurrentVenueTypePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue]);

  const categorySections = useMemo(() => {
    return ["All", ...(categories?.map((category) => category.title) || [])];
  }, [categories]);

  let filteredBusinessesList = useMemo(() => {
    const transformBusiness = (business: any) => ({
      ...business,
      business: {
        ...business.business,
        qrCode: business.business.qr_code,
      },
      photoThumb: business.photo_thumb || "",
      openingHours:
        business.opening_hours?.map((hour: any) => ({
          googleBusinessId: hour.google_business_id,
          day: hour.day,
          openTime: hour.open_time,
          closeTime: hour.close_time,
          id: hour.id,
          utcOffset: hour.utc_offset,
          openTimeUtc: hour.open_time_utc,
          closeTimeUtc: hour.close_time_utc,
          createdAt: hour.created_at,
          updatedAt: hour.updated_at,
          mode: hour.mode,
        })) || [],
      latestReviewDateUpdated: business.latest_review_date_updated || "",
      tagIds: business.tag_ids || "",
      name: business.name || "",
      address: business.address || "",
    });

    if (selectedSection === "All") {
      return filteredNearbyBusinesses.map(transformBusiness);
    }
    return filteredNearbyBusinesses
      .filter((business) =>
        business.nearbyBusinessData?.some(
          (nearby) => nearby.business_nearby_category_title === selectedSection
        )
      )
      .map(transformBusiness);
  }, [selectedSection, filteredNearbyBusinesses]);

  // Apply search filter if search term has 3+ characters
  if (searchTerm.length >= 3) {
    const searchLower = searchTerm.toLowerCase();
    filteredBusinessesList = filteredBusinessesList.filter((business) => {
      console.log("business", business);
      const name = business?.name?.toLowerCase() || "";
      const address = business?.address?.toLowerCase() || "";

      return name.includes(searchLower) || address.includes(searchLower);
    });
  }
  const filteredBusinesses = filteredBusinessesList;

  const handleCategorySectionTracking = (section: string) => {
    AnalyticsService.trackGAEventHandler("navigatedToNearbyVenuesCategory");
    AnalyticsService.trackMx("navigatedToNearbyVenuesCategory", {
      Category: section,
    });
  };

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [refetch, user]);

  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode
    );

    AnalyticsService.trackGAEventHandler(
      "nearbyVenuesListmySavedPlacesExitSave"
    );
    AnalyticsService.trackMx("nearbyVenuesListmySavedPlacesExitSave", {});

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button  and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const onVenueClick = (location: Venue) => {
    console.log("onVenueClick nearby venues", location);
    AnalyticsService.trackGAEventHandler("nearbyVenuesListClickedViewVenue");
    AnalyticsService.trackMx("nearbyVenuesListClickedViewVenue", {
      VenueId: location.id,
      VenueBrandId: location.brandId,
      VenueBrandName: location.brandName,
      VenueAddress: location.address,
      VenueName: location.name,
      VenueType: location.businessType,
    });
    setCurrentVenueTypePage(1);
    track.current?.next();
    trackMobile.current?.next();
    setTimeout(() => {
      setSelectedVenue(location);
    }, 400);
  };

  if (
    !categories ||
    categories.length === 0 ||
    !nearbyBusinessList ||
    nearbyBusinessList.length === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-center paragraph-small-regular text-primary-500 dark:text-primary-300">
          No nearby venues found
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-white dark:bg-primary-900 flex flex-col">
      {currentVenueTypePage === 0 && categorySections.length > 1 && (
        <div className="w-full py-2 flex-shrink-0">
          {/* Search textbox - Full width on mobile only */}
          <div className="w-full px-[16px] mb-5 md:hidden">
            <FloatingLabelInput
              label={i18n.get("searchNearbyVenues")}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => {
                setSearchTerm("");
              }}               
            />              
          </div>

          <div className="flex items-center">
            {/* Search textbox - Inline for desktop */}
            <div className="hidden md:flex pl-[16px] pr-[20px] items-center space-x-4 md:space-x-6 mb-5">
              <FloatingLabelInput
                label={i18n.get("searchNearbyVenues")}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => {
                  setSearchTerm("");
                }}               
              />              
            </div>

            {/* Scrollable section */}
            <div className="overflow-x-auto flex-1">
              <ScrollContainer>
                <div className="flex flex-row min-w-max space-x-4 md:space-x-6 mb-5 pl-[16px] md:pl-0">
                  {categorySections.map((section) => (
                    <button
                      key={section}
                      className={cn(
                        "py-[10px] px-[16px] rounded-[20px] whitespace-nowrap text-primary-800 dark:text-primary-100 paragraph-small-medium",
                        selectedSection === section
                          ? "bg-secondary-400 dark:text-primary-900"
                          : "bg-primary-100 dark:bg-[#37354D]"
                      )}
                      onClick={() => {
                        handleCategorySectionTracking(section);
                        setSelectedSection(section);
                      }}
                      aria-label={`${section} ${selectedSection === section ? "selected" : ""} `}
                    >
                      {section}
                    </button>
                  ))}
                </div>
              </ScrollContainer>
            </div>

            <NearbyFilterbar
              onFilters={() => {
                AnalyticsService.trackGAEventHandler(
                  "nearbyVenuesListOpenFilters"
                );
                AnalyticsService.trackMx("nearbyVenuesListOpenFilters", {});
                setIsFilterSheetOpen(true);
              }}
            />

            {/* Mobile view switch icon */}
            <div
              id="mobileSwitchIcons"
              onClick={() => {
                setMobileListView(!mobileListView);
              }}
              role="button"
              aria-label={
                mobileListView
                  ? i18n.get("switchToMapView") || "Switch to map view"
                  : i18n.get("switchToListView") || "Switch to list view"
              }
              className="flex md:hidden p-2 ml-4 mb-5 bg-primary-50 rounded-full shrink-0"
            >
              {mobileListView ? (
                <MapTrifold size={24} className="text-primary-900" />
              ) : (
                <ListBullets size={24} className="text-primary-900" />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="hidden md:flex flex-row flex-1 overflow-hidden max-h-[calc(100vh-60px)]">
        <div
          className={cn(
            "transition duration-100 flex-1 flex flex-col w-[50%]",
            isLeftPaneVisible ? "flex" : "hidden"
          )}
        >
          {currentVenueTypePage === 1 && (
            <div
              tabIndex={0}
              onClick={() => {
                track.current?.prev();
                setCurrentVenueTypePage(0);
                setTimeout(() => {
                  setSelectedVenue(null);
                }, 500);
              }}
              role="button"
              aria-label={i18n.get("backToVenueList") || "Back to venue list"}
              className="flex flex-row items-center pb-3 cursor-pointer flex-shrink-0"
            >
              <ArrowLeft
                size={24}
                className="!text-primary-500 dark:!text-primary-300"
                aria-hidden="true"
              />
              <p className="ml-1 paragraph-small-semibold !text-primary-500 dark:!text-primary-300">
                {i18n.get("back")}
              </p>
            </div>
          )}

          <div
            ref={venueDetailsScrollRef}
            className={cn("overflow-y-auto flex-1 pb-6")}
            onScroll={(e) => {
              const scrollPosition = e.currentTarget.scrollTop;
              setScrollToTopVisible(scrollPosition > 100);
            }}
          >
            <ViewPager tag="div" tabIndex={-1}>
              <Frame className="frame">
                <Track
                  ref={track}
                  viewsToShow={1}
                  className="track"
                  swipe={false}
                >
                  <View aria-label="Venue List" role="list">
                    {currentVenueTypePage === 0 && (
                      <>
                        {filteredBusinesses.length > 0 ? (
                          <div className="grid pb-10 md:grid-cols-2 gap-3 md:gap-3">
                            {filteredBusinesses.map((business) => (
                              <VenueCard
                                key={business.id}
                                location={business}
                                onOpen={onVenueClick}
                                onSaveVenue={handleSaveVenue}
                                savedPlaces={savedPlaces}
                                isNearbyBusinessCard={true}
                              />
                            ))}
                          </div>
                        ) : filtersState.isFiltersApplied ? (
                          <div className="w-full">
                            <NearbyVenuesNoFilterResults />
                          </div>
                        ) : (
                          <div className="w-full">
                            <NearbyVenuesNoCategoryResults
                              handleCategorySectionTracking={
                                handleCategorySectionTracking
                              }
                              setSelectedSection={setSelectedSection}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </View>
                  <View className="">
                    {selectedVenue && (
                      <div className="pr-[16px]">
                        <VenueDetails
                          selectedVenue={selectedVenue}
                          onBack={() => {
                            track.current?.prev();
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                          }}
                          openAllPhotosInline={true}
                        />
                      </div>
                    )}
                  </View>
                </Track>
              </Frame>
            </ViewPager>
          </div>
          {scrollToTopVisible && (
            <div
              onClick={() => {
                venueDetailsScrollRef?.current?.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
              role="button"
              aria-label={i18n.get("scrollToTop") || "Scroll to top"}
              className="bg-primary-800 dark:bg-[#4640BE] rounded-sm fixed right-6 bottom-6 w-[30px] h-[30px] flex items-center justify-center cursor-pointer shadow-md back-to-top-arrow-nearby z-50"
            >
              <ArrowUp color="#fff" size={20} aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="flex-1 w-[50%] overflow-hidden">
          <MapViewForNearby
            filteredBusinesses={filteredBusinesses}
            venueLocation={{
              latitude: venueLatitude,
              longitude: venueLongitude,
            }}
            venueId={venueId}
            hidden={false}
          />
        </div>
      </div>

      {/* MOBILE */}
      <div className="flex md:hidden flex-col flex-1 w-full overflow-hidden">
        {currentVenueTypePage === 1 && (
          <div
            tabIndex={0}
            onClick={() => {
              trackMobile.current?.prev();
              setCurrentVenueTypePage(0);
              setTimeout(() => {
                setSelectedVenue(null);
              }, 500);
            }}
            role="button"
            aria-label={i18n.get("backToVenueList") || "Back to venue list"}
            className="flex flex-row items-center pb-3 cursor-pointer ml-[20px] flex-shrink-0"
          >
            <ArrowLeft
              size={24}
              className="!text-primary-500 dark:!text-primary-300"
              aria-hidden="true"
            />
            <p className="ml-1 paragraph-small-semibold !text-primary-500 dark:!text-primary-300">
              {i18n.get("back")}
            </p>
          </div>
        )}

        <div
          ref={mobileVenueDetailsScrollRef}
          className={cn(
            "flex-1",
            mobileListView ? "overflow-y-auto pb-6" : "overflow-hidden"
          )}
        >
          <div className={cn(mobileListView ? "block" : "hidden")}>
            <ViewPager tag="div" tabIndex={-1}>
              <Frame className="frame">
                <Track
                  ref={trackMobile}
                  viewsToShow={1}
                  className="track"
                  swipe={false}
                >
                  <View>
                    {currentVenueTypePage === 0 && (
                      <>
                        {filteredBusinesses.length > 0 ? (
                          <div className="grid pb-10 md:grid-cols-2 gap-3 md:gap-3">
                            {filteredBusinesses.map((business) => (
                              <VenueCard
                                key={business.id}
                                location={business}
                                onOpen={onVenueClick}
                                onSaveVenue={handleSaveVenue}
                                savedPlaces={savedPlaces}
                              />
                            ))}
                          </div>
                        ) : filtersState.isFiltersApplied ? (
                          <div className="w-full">
                            <NearbyVenuesNoFilterResults />
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-8 w-full">
                            <p className="text-center paragraph-small-regular text-primary-500 dark:text-primary-300">
                              No nearby venues found
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </View>
                  <View className="">
                    {selectedVenue && (
                      <VenueDetails
                        selectedVenue={selectedVenue}
                        onBack={() => {
                          trackMobile.current?.prev();
                          setCurrentVenueTypePage(0);
                          setTimeout(() => {
                            setSelectedVenue(null);
                          }, 500);
                        }}
                        openAllPhotosInline={true}
                      />
                    )}
                  </View>
                </Track>
              </Frame>
            </ViewPager>
          </div>
          <MapViewForNearby
            venueLocation={{
              latitude: venueLatitude,
              longitude: venueLongitude,
            }}
            filteredBusinesses={filteredBusinesses}
            venueId={venueId}
            hidden={mobileListView}
          />
        </div>
      </div>

      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={location}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={location}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      <NearbyFiltersSheet
        open={isFilterSheetOpen}
        nearbyFilterAccessibility={nearbyFilterAccessibility}
        accessibilityFeaturesRequirementsHiddenIds={
          accessibilityFeaturesRequirementsHiddenIds
        }
        accessibilityFeaturesRequirementsShowHiddenById={
          accessibilityFeaturesRequirementsShowHiddenById
        }
        onClose={() => {
          AnalyticsService.trackGAEventHandler("nearbyVenuesListCloseFilters");
          AnalyticsService.trackMx("nearbyVenuesListCloseFilters", {});
          setIsFilterSheetOpen(false);
        }}
      />
    </div>
  );
}
